import React, { useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkouts } from "../../contexts/WorkoutContext";
import Records from "./Records";
import UserWorkouts from "./UserWorkouts";
import UserVideoLibrary from "./UserVideoLibrary";
import UserGoals from "./UserGoals";
import Timer from "./Timer";
import MessagesPanel from "./MessagesPanel";
import PBToast from "./PBToast";
import {
	Home,
	Dumbbell,
	Settings,
	LogOut,
	Timer as TimerIcon,
	ChevronDown,
	ChevronUp,
	User,
	Lock,
	Mail,
	Calendar,
	Check,
	ClipboardList,
	Video,
	Bell,
	Target,
} from "lucide-react";
import CustomModal from "./CustomModal";

const SettingsPanel = ({
	user,
	updateOwnProfile,
	changeOwnPassword,
	loading,
}) => {
	const [name, setName] = useState(user?.name || "");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [nameSuccess, setNameSuccess] = useState(false);
	const [passwordSuccess, setPasswordSuccess] = useState(false);
	const [saving, setSaving] = useState(false);
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});

	const handleNameUpdate = async (e) => {
		e.preventDefault();
		if (!name.trim()) {
			setModal({
				show: true,
				title: "Invalid Name",
				message: "Name cannot be empty.",
				type: "warning",
			});
			return;
		}
		setSaving(true);
		try {
			await updateOwnProfile({ name: name.trim() });
			setNameSuccess(true);
			setTimeout(() => setNameSuccess(false), 3000);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to update name.",
				type: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	const handlePasswordChange = async (e) => {
		e.preventDefault();
		if (newPassword.length < 6) {
			setModal({
				show: true,
				title: "Invalid Password",
				message: "Password must be at least 6 characters.",
				type: "warning",
			});
			return;
		}
		if (newPassword !== confirmPassword) {
			setModal({
				show: true,
				title: "Mismatch",
				message: "Passwords do not match.",
				type: "warning",
			});
			return;
		}
		setSaving(true);
		try {
			await changeOwnPassword(newPassword);
			setNewPassword("");
			setConfirmPassword("");
			setPasswordSuccess(true);
			setTimeout(() => setPasswordSuccess(false), 3000);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to change password.",
				type: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	const memberSince = user?.createdAt
		? new Date(
				user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt,
			).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "long",
				year: "numeric",
			})
		: "N/A";

	return (
		<div>
			<h5 className="mb-4" style={{ color: "#fff" }}>
				<Settings size={20} className="me-2" />
				Settings
			</h5>

			{/* Account Info */}
			<div
				className="mb-4 p-3"
				style={{
					backgroundColor: "#2a2a2a",
					borderRadius: "8px",
					border: "1px solid #333",
				}}
			>
				<h6 style={{ color: "#d15a36", marginBottom: "1rem" }}>
					Account Information
				</h6>
				<div className="row g-3">
					<div className="col-12 col-sm-6">
						<div className="d-flex align-items-center gap-2 mb-1">
							<Mail size={14} style={{ color: "rgba(255,255,255,0.5)" }} />
							<small style={{ color: "rgba(255,255,255,0.5)" }}>Email</small>
						</div>
						<div style={{ color: "#fff" }}>{user?.email}</div>
					</div>
					<div className="col-12 col-sm-6">
						<div className="d-flex align-items-center gap-2 mb-1">
							<Calendar size={14} style={{ color: "rgba(255,255,255,0.5)" }} />
							<small style={{ color: "rgba(255,255,255,0.5)" }}>
								Member Since
							</small>
						</div>
						<div style={{ color: "#fff" }}>{memberSince}</div>
					</div>
				</div>
			</div>

			{/* Name Change */}
			<div
				className="mb-4 p-3"
				style={{
					backgroundColor: "#2a2a2a",
					borderRadius: "8px",
					border: "1px solid #333",
				}}
			>
				<h6 style={{ color: "#d15a36", marginBottom: "1rem" }}>
					<User size={16} className="me-2" />
					Change Name
				</h6>
				<form onSubmit={handleNameUpdate}>
					<div className="row g-2 align-items-end">
						<div className="col">
							<label
								className="form-label small"
								style={{ color: "rgba(255,255,255,0.6)" }}
							>
								Display Name
							</label>
							<input
								type="text"
								className="form-control"
								value={name}
								onChange={(e) => setName(e.target.value)}
								disabled={saving || loading}
								style={{
									backgroundColor: "#1a1a1a",
									border: "1px solid #444",
									color: "#fff",
								}}
							/>
						</div>
						<div className="col-auto">
							<button
								type="submit"
								className="btn"
								disabled={saving || loading || name.trim() === user?.name}
								style={{
									backgroundColor: "#d15a36",
									color: "#fff",
									border: "none",
									opacity:
										saving || loading || name.trim() === user?.name ? 0.5 : 1,
								}}
							>
								{saving ? (
									"Saving..."
								) : nameSuccess ? (
									<>
										<Check size={16} className="me-1" />
										Saved
									</>
								) : (
									"Update"
								)}
							</button>
						</div>
					</div>
				</form>
			</div>

			{/* Password Change */}
			<div
				className="p-3"
				style={{
					backgroundColor: "#2a2a2a",
					borderRadius: "8px",
					border: "1px solid #333",
				}}
			>
				<h6 style={{ color: "#d15a36", marginBottom: "1rem" }}>
					<Lock size={16} className="me-2" />
					Change Password
				</h6>
				<form onSubmit={handlePasswordChange}>
					<div className="row g-3">
						<div className="col-12 col-sm-6">
							<label
								className="form-label small"
								style={{ color: "rgba(255,255,255,0.6)" }}
							>
								New Password
							</label>
							<input
								type="password"
								className="form-control"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								placeholder="Min 6 characters"
								minLength={6}
								disabled={saving || loading}
								style={{
									backgroundColor: "#1a1a1a",
									border: "1px solid #444",
									color: "#fff",
								}}
							/>
						</div>
						<div className="col-12 col-sm-6">
							<label
								className="form-label small"
								style={{ color: "rgba(255,255,255,0.6)" }}
							>
								Confirm Password
							</label>
							<input
								type="password"
								className="form-control"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								placeholder="Repeat password"
								minLength={6}
								disabled={saving || loading}
								style={{
									backgroundColor: "#1a1a1a",
									border: "1px solid #444",
									color: "#fff",
								}}
							/>
						</div>
						<div className="col-12">
							<button
								type="submit"
								className="btn"
								disabled={saving || loading || !newPassword || !confirmPassword}
								style={{
									backgroundColor: "#d15a36",
									color: "#fff",
									border: "none",
									opacity:
										saving || loading || !newPassword || !confirmPassword
											? 0.5
											: 1,
								}}
							>
								{saving ? (
									"Changing..."
								) : passwordSuccess ? (
									<>
										<Check size={16} className="me-1" />
										Password Changed
									</>
								) : (
									"Change Password"
								)}
							</button>
						</div>
					</div>
				</form>
			</div>
			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
		</div>
	);
};

const UserDashboard = () => {
	const {
		user,
		logout,
		updateOwnProfile,
		changeOwnPassword,
		loading: authLoading,
	} = useAuth();
	const {
		sharedWorkouts,
		userRecords,
		movements,
		countTypes,
		notifications,
		workoutScores,
		addUserRecord,
		deleteUserRecord,
		updateUserRecord,
		addWorkoutScore,
		deleteWorkoutScore,
		workshops,
		userGoals,
		goalDeadline,
		addGoal,
		updateGoal,
		deleteGoal,
		loading,
		pbToast,
		setPbToast,
	} = useWorkouts();

	const dismissPbToast = useCallback(() => setPbToast(null), [setPbToast]);

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, workouts, records, timer
	const [expandedNotifications, setExpandedNotifications] = useState({});
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const toggleNotification = (notificationId) => {
		setExpandedNotifications((prev) => ({
			...prev,
			[notificationId]: !prev[notificationId],
		}));
	};

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Logout error:", error);
		}
	};

	const handleAddRecord = async (recordData) => {
		try {
			await addUserRecord(recordData);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save movement.",
				type: "error",
			});
		}
	};

	const handleDeleteRecord = async (recordId) => {
		try {
			await deleteUserRecord(recordId);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to delete movement.",
				type: "error",
			});
		}
	};

	const handleEditRecord = async (recordId, recordData) => {
		try {
			await updateUserRecord(recordId, recordData);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to update movement.",
				type: "error",
			});
		}
	};

	const sidebarItems = [
		{
			icon: Home,
			label: "Dashboard",
			active: activeTab === "dashboard",
			onClick: () => {
				setActiveTab("dashboard");
				setSidebarOpen(false);
			},
		},
		{
			icon: ClipboardList,
			label: "Workouts",
			active: activeTab === "workouts",
			onClick: () => {
				setActiveTab("workouts");
				setSidebarOpen(false);
			},
		},
		{
			icon: Dumbbell,
			label: "Movements",
			active: activeTab === "records",
			onClick: () => {
				setActiveTab("records");
				setSidebarOpen(false);
			},
		},
		{
			icon: Video,
			label: "Video Library",
			active: activeTab === "workshops",
			onClick: () => {
				setActiveTab("workshops");
				setSidebarOpen(false);
			},
		},
		{
			icon: Target,
			label: "Goals",
			active: activeTab === "goals",
			onClick: () => {
				setActiveTab("goals");
				setSidebarOpen(false);
			},
		},
		{
			icon: TimerIcon,
			label: "Timer",
			active: activeTab === "timer",
			onClick: () => {
				setActiveTab("timer");
				setSidebarOpen(false);
			},
		},
		{
			icon: Settings,
			label: "Settings",
			active: activeTab === "settings",
			onClick: () => {
				setActiveTab("settings");
				setSidebarOpen(false);
			},
		},
	];

	// Calculate user stats
	const totalRecords = userRecords.length;

	// Get unique movements from records
	const uniqueMovements = new Set(userRecords.map((record) => record.movement))
		.size;

	return (
		<div className="dashboard-container">
			{/* Left Sidebar */}
			<div
				className={`dashboard-sidebar${
					sidebarOpen ? " dashboard-sidebar--open" : ""
				}`}
			>
				<div className="dashboard-sidebar-header">
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
						alt="Urban 59 Logo"
						className="dashboard-sidebar-logo"
						style={{
							maxWidth: "180px",
							height: "auto",
							display: "block",
							margin: "0 auto 0rem auto",
						}}
					/>
				</div>

				<nav className="dashboard-sidebar-nav">
					{sidebarItems.map((item, index) => {
						const IconComponent = item.icon;
						return (
							<button
								key={index}
								onClick={item.onClick}
								className={`dashboard-nav-item ${item.active ? "active" : ""}`}
								style={{
									background: "none",
									border: "none",
									width: "100%",
									textAlign: "left",
								}}
								disabled={!item.onClick}
							>
								<span className="dashboard-nav-icon">
									<IconComponent size={20} />
								</span>
								<span className="dashboard-nav-label">{item.label}</span>
							</button>
						);
					})}
				</nav>

				<div className="dashboard-sidebar-footer">
					<button className="dashboard-logout-btn" onClick={handleLogout}>
						<span className="dashboard-nav-icon">
							<LogOut size={20} />
						</span>
						<span className="dashboard-nav-label">Logout</span>
					</button>
				</div>
			</div>

			{/* Main Content */}
			<div className="dashboard-main-content">
				{/* Top Header */}
				<div className="dashboard-top-header">
					{!sidebarOpen && (
						<button
							className="dashboard-sidebar-toggle"
							onClick={() => setSidebarOpen((open) => !open)}
							aria-label="Toggle sidebar"
						>
							<span style={{ fontSize: 28, lineHeight: 1 }}>&#9776;</span>
						</button>
					)}
					{sidebarOpen && (
						<div
							className="dashboard-sidebar-backdrop"
							onClick={() => setSidebarOpen(false)}
						></div>
					)}
					<div className="dashboard-header-left">
						<p className="dashboard-page-subtitle">
							Welcome back to your Urban 59 Dashboard, {user?.name}!
						</p>
					</div>
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoAsset 3@4x.png"}
						alt="Urban 59"
						className="dashboard-header-mobile-logo"
					/>
					<div className="dashboard-header-right">
						<div
							className="dashboard-user-profile"
							style={{ gap: 12, display: "flex", alignItems: "center" }}
						>
							<div
								className="dashboard-user-info"
								style={{ textAlign: "right" }}
							>
								<h6 className="dashboard-user-name" style={{ margin: 0 }}>
									{user?.name || "User"}
								</h6>
								<small className="dashboard-user-email">{user?.email}</small>
							</div>
							<div className="dashboard-user-avatar">
								<span className="dashboard-user-initial">
									{(
										user?.name?.charAt(0) ||
										user?.email?.charAt(0) ||
										"U"
									).toUpperCase()}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Stats Cards */}
				<div className="dashboard-stats-grid">
					<div
						className="dashboard-stat-card primary"
						onClick={() => setActiveTab("records")}
						style={{ cursor: "pointer" }}
					>
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">Movements</h3>
								<h2 className="dashboard-stat-number">{totalRecords}</h2>
								<small className="dashboard-stat-change positive">
									{uniqueMovements} movements tracked
								</small>
							</div>
							<div className="dashboard-stat-icon">
								<Dumbbell size={32} />
							</div>
						</div>
					</div>

					{/* Workouts Card */}
					<div
						className="dashboard-stat-card"
						onClick={() => setActiveTab("workouts")}
						style={{ cursor: "pointer" }}
					>
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">Workouts</h3>
								<h2 className="dashboard-stat-number">
									{workoutScores.length}
								</h2>
								<small className="dashboard-stat-change positive">
									{workoutScores.length === 1
										? "1 workout"
										: `${workoutScores.length} workouts`}{" "}
									completed
								</small>
							</div>
							<div className="dashboard-stat-icon">
								<ClipboardList size={32} />
							</div>
						</div>
					</div>

					{/* Timer Shortcut Card - hidden on mobile */}
					<div
						className="dashboard-stat-card dashboard-hide-mobile"
						onClick={() => setActiveTab("timer")}
						style={{ cursor: "pointer" }}
					>
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">Timer</h3>
								<h2
									className="dashboard-stat-number"
									style={{ fontSize: "1.25rem" }}
								>
									Start Timer
								</h2>
								<small className="dashboard-stat-change positive">
									Workout timer
								</small>
							</div>
							<div className="dashboard-stat-icon">
								<TimerIcon size={32} />
							</div>
						</div>
					</div>
				</div>

				{/* Content */}
				<div className="dashboard-content-grid-full">
					<div className="dashboard-content-main">
						<div className="card">
							{/* Workouts tab header hidden */}

							<div className="card-body">
								{/* Workouts Tab */}
								{activeTab === "workouts" && (
									<UserWorkouts
										sharedWorkouts={sharedWorkouts}
										workoutScores={workoutScores}
										addWorkoutScore={addWorkoutScore}
										deleteWorkoutScore={deleteWorkoutScore}
										loading={loading}
									/>
								)}

								{/* Timer Tab */}
								{activeTab === "timer" && (
									<div>
										<div className="d-flex justify-content-between align-items-center mb-3">
											<h5 className="mb-0">
												<TimerIcon className="me-2" size={20} />
												Workout Timer
											</h5>
										</div>
										<Timer />
									</div>
								)}

								{/* Video Library Tab */}
								{activeTab === "workshops" && (
									<UserVideoLibrary workshops={workshops} loading={loading} />
								)}

								{/* Goals Tab */}
								{activeTab === "goals" && (
									<UserGoals
										goals={userGoals}
										goalDeadline={goalDeadline}
										onAddGoal={addGoal}
										onUpdateGoal={updateGoal}
										onDeleteGoal={deleteGoal}
										loading={loading}
									/>
								)}

								{/* Movements Tab */}
								{activeTab === "records" && (
									<div>
										<div className="d-flex justify-content-between align-items-center mb-3">
											<h5 className="mb-0">
												<Dumbbell className="me-2" size={20} />
												Movements
											</h5>
										</div>
										<Records
											userRecords={userRecords}
											movements={movements}
											countTypes={countTypes}
											onAddRecord={handleAddRecord}
											onDeleteRecord={handleDeleteRecord}
											onEditRecord={handleEditRecord}
											loading={loading}
										/>
									</div>
								)}

								{/* Workout Form hidden */}

								{/* Dashboard View - News & Notifications + Messages */}
								{activeTab === "dashboard" && (
									<div className="row g-3">
										<div className="col-lg-6">
											<div
												style={{
													overflow: "hidden",
													backgroundColor: "#2a2a2a",
													borderRadius: "8px",
													border: "1px solid #333",
													padding: "1rem",
												}}
											>
												<h6 className="mb-3 d-flex align-items-center">
													<Bell
														size={18}
														className="me-2"
														style={{ color: "#d15a36" }}
													/>
													News & Notifications
												</h6>
												{notifications.length === 0 ? (
													<div className="text-center py-5">
														<p className="text-muted">
															No news or notifications at the moment.
														</p>
													</div>
												) : (
													<div>
														{notifications.map((notification) => {
															const isExpanded =
																expandedNotifications[notification.id];
															const typeBadgeStyle =
																notification.type === "urgent"
																	? { backgroundColor: "#dc3545" }
																	: notification.type === "important"
																		? {
																				backgroundColor: "#ffc107",
																				color: "#1a1a1a",
																			}
																		: { backgroundColor: "#d15a36" };

															return (
																<div
																	key={notification.id}
																	className="mb-3"
																	style={{
																		borderRadius: "8px",
																		overflow: "hidden",
																		border: isExpanded
																			? "1px solid #d15a36"
																			: "1px solid #333",
																		backgroundColor: "#1a1a1a",
																		transition: "border-color 0.2s ease",
																	}}
																>
																	{/* Header */}
																	<div
																		className="p-3"
																		onClick={() =>
																			toggleNotification(notification.id)
																		}
																		style={{
																			cursor: "pointer",
																			borderBottom: isExpanded
																				? "2px solid #d15a36"
																				: "none",
																		}}
																	>
																		<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
																			<div style={{ flex: 1, minWidth: 0 }}>
																				<h6
																					className="mb-0"
																					style={{
																						color: "#fff",
																						wordBreak: "break-word",
																						fontSize: "1rem",
																					}}
																				>
																					{notification.title}
																				</h6>
																			</div>
																			<div className="d-flex align-items-center flex-shrink-0">
																				{isExpanded ? (
																					<ChevronUp
																						size={18}
																						style={{ color: "#d15a36" }}
																					/>
																				) : (
																					<ChevronDown
																						size={18}
																						style={{
																							color: "rgba(255,255,255,0.5)",
																						}}
																					/>
																				)}
																			</div>
																		</div>

																		{/* Meta row */}
																		<div className="d-flex flex-wrap align-items-center gap-2">
																			<span
																				className="badge"
																				style={{
																					...typeBadgeStyle,
																					fontSize: "0.7rem",
																				}}
																			>
																				{notification.type}
																			</span>
																			<small
																				style={{
																					color: "rgba(255,255,255,0.45)",
																				}}
																			>
																				{new Date(
																					notification.createdAt,
																				).toLocaleDateString()}
																			</small>
																			<small
																				style={{
																					color: "rgba(255,255,255,0.35)",
																				}}
																			>
																				by {notification.createdBy}
																			</small>
																		</div>
																	</div>

																	{/* Expanded content */}
																	{isExpanded && (
																		<div
																			className="p-3"
																			style={{ overflow: "hidden" }}
																		>
																			<p
																				className="mb-0"
																				style={{
																					color: "rgba(255,255,255,0.7)",
																					fontSize: "0.9rem",
																					lineHeight: 1.6,
																					wordBreak: "break-word",
																				}}
																			>
																				{notification.content}
																			</p>
																		</div>
																	)}
																</div>
															);
														})}
													</div>
												)}
											</div>
										</div>
										<div className="col-lg-6">
											<div
												style={{
													overflow: "hidden",
													backgroundColor: "#2a2a2a",
													borderRadius: "8px",
													border: "1px solid #333",
													padding: "1rem",
												}}
											>
												<MessagesPanel />
											</div>
										</div>
									</div>
								)}

								{/* Workouts Tab hidden */}

								{/* Settings Tab */}
								{activeTab === "settings" && (
									<SettingsPanel
										user={user}
										updateOwnProfile={updateOwnProfile}
										changeOwnPassword={changeOwnPassword}
										loading={authLoading}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			{pbToast && (
				<PBToast
					movement={pbToast.movement}
					value={pbToast.value}
					unit={pbToast.unit}
					onDismiss={dismissPbToast}
				/>
			)}
		</div>
	);
};

export default UserDashboard;
